/**
 * I pezzi del telefono che servono a tutte le pagine dell'Amministrazione e a
 * quelle che le girano intorno (fattura, preventivo, report, abbonamenti).
 * 7/10/2026, con la regola di mobile.md §12: ogni pagina risponde a UNA
 * domanda, il resto sta un tocco più in là.
 *
 * Si appoggiano al kit (components/mobile/MobileKit.tsx) senza toccarlo. Il
 * pezzo che si ripete in tutte le pagine è il FOGLIO DI UNA RIGA: si tocca una
 * fattura, una spesa, una tassa, e sale un pannello con i fatti (etichetta a
 * sinistra, valore a destra, come nelle impostazioni dell'iPhone) e i gesti
 * (un verbo per riga, quello principale in fondo). È lì che stanno i gesti
 * veloci che prima erano tre bottoni dentro ogni scheda.
 *
 * Il computer non disegna mai questi componenti.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { MobileSheet } from '../../../components/ui/MobileSheet';
import { MRow, leggibile } from '../../../components/mobile/MobileKit';
import type { IconName } from '../../../components/ui/Icon';
import { fmtEur } from '../../../lib/utils';
import { daysFromToday } from '../adminApi';
import { nomeBreve } from '../fatture/fattureApi';
import './admin-mobile.css';

/**
 * Il nome di un cliente o di un fornitore nella riga: leggibile (non tutto
 * maiuscolo) e breve (senza S.r.l. né "di Tizio" in coda). "NEW OMNIA S.R.L."
 * → "New Omnia", "Run Factory di Urso Salvatore" → "Run Factory". Il nome
 * intero sta nel titolo del pannello che si apre.
 */
export function nomeRiga(nome: string): string {
  return nomeBreve(leggibile(nome)) || nome;
}

// ════════════════════════════════════════════════════════════════════
// Numeri e date, detti come si dicono
// ════════════════════════════════════════════════════════════════════

/** "€ 1.234", "−€ 4.670": il segno davanti, come in Situazione. */
export function euro(n: number): string {
  return `${n < 0 ? '−' : ''}€ ${fmtEur(Math.abs(n))}`;
}

/** I centesimi solo quando ci sono: "€ 22", "€ 15,85", "€ 1.000,40". */
export function euroEsatto(n: number): string {
  const tondo = Math.round(Math.abs(n) * 100) % 100 === 0;
  return `${n < 0 ? '−' : ''}€ ${fmtEur(Math.abs(n), !tondo)}`;
}

/**
 * L'importo a destra di una riga: sotto i 100 euro con i centesimi (€ 15,85
 * detto € 16 sarebbe un altro numero), sopra arrotondato (€ 1.000,40 → € 1.000):
 * nella riga si confronta, i centesimi esatti stanno nel foglio.
 */
export function euroRiga(n: number): string {
  return Math.abs(n) < 100 ? euroEsatto(n) : euro(n);
}

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

function parti(iso: string | null | undefined): [number, number, number] | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return y && m && d ? [y, m, d] : null;
}

const annoOggi = () => new Date().getFullYear();

/** "5 ottobre", "5 ottobre 2025" (l'anno solo se non è quello di oggi). */
export function giornoBreve(iso: string | null | undefined): string {
  const p = parti(iso);
  if (!p) return '';
  return `${p[2]} ${MESI[p[1] - 1]}${p[0] !== annoOggi() ? ` ${p[0]}` : ''}`;
}

/** Il titolo di un gruppo per giorno: "Oggi", "Ieri", "Lunedì 5 ottobre", "5 ottobre 2025". */
export function giornoGruppo(iso: string | null | undefined): string {
  const p = parti(iso);
  if (!p) return 'Senza data';
  const g = daysFromToday(iso);
  if (g === 0) return 'Oggi';
  if (g === -1) return 'Ieri';
  if (g === 1) return 'Domani';
  if (p[0] !== annoOggi()) return `${p[2]} ${MESI[p[1] - 1]} ${p[0]}`;
  const dow = GIORNI[new Date(p[0], p[1] - 1, p[2]).getDay()]!;
  return `${dow.charAt(0).toUpperCase()}${dow.slice(1)} ${p[2]} ${MESI[p[1] - 1]}`;
}

/** Il titolo di un gruppo per mese: "Ottobre", "Dicembre 2025". Da "2026-10" o da una data. */
export function meseGruppo(isoOrYm: string | null | undefined): string {
  const p = parti(isoOrYm && isoOrYm.length === 7 ? `${isoOrYm}-01` : isoOrYm);
  if (!p) return 'Senza data';
  const nome = MESI[p[1] - 1]!;
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}${p[0] !== annoOggi() ? ` ${p[0]}` : ''}`;
}

/** Il nome di un mese, minuscolo: "ottobre". */
export const nomeMese = (m: number) => MESI[m - 1] ?? '';

/** Divide una lista già ordinata in gruppi consecutivi con la stessa chiave. */
export function aGruppi<T>(items: readonly T[], chiave: (x: T) => string): Array<{ chiave: string; items: T[] }> {
  const out: Array<{ chiave: string; items: T[] }> = [];
  for (const it of items) {
    const k = chiave(it);
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo.chiave === k) ultimo.items.push(it);
    else out.push({ chiave: k, items: [it] });
  }
  return out;
}

// ════════════════════════════════════════════════════════════════════
// Il foglio di una riga
// ════════════════════════════════════════════════════════════════════

/**
 * Il pannello che sale quando si tocca una riga. `primary` è il gesto
 * principale (un bottone pieno, in fondo); dentro, gruppi di fatti e di gesti.
 */
export function Foglio({ open, onClose, title, primary, children }: {
  open: boolean;
  onClose: () => void;
  title?: string;
  primary?: ReactNode;
  children: ReactNode;
}) {
  return (
    <MobileSheet open={open} onClose={onClose} title={title} footer={primary}>
      <div className="mk amk-foglio">{children}</div>
    </MobileSheet>
  );
}

/** Un fatto: l'etichetta a sinistra, il valore a destra. Solo dentro un foglio o un dettaglio. */
export function Fatto({ label, value, alert }: { label: ReactNode; value: ReactNode; alert?: boolean }) {
  return (
    <div className="mk-row amk-fatto">
      <span className="amk-fatto-k">{label}</span>
      <span className={`amk-fatto-v${alert ? ' is-alert' : ''}`}>{value}</span>
    </div>
  );
}

/** Un gesto: icona e verbo. Quelli che tolgono o cancellano sono rossi. */
export function Gesto({ icon, label, sub, onClick, danger, disabled }: {
  icon: IconName;
  label: string;
  sub?: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <MRow
      icon={icon}
      tone={danger ? 'alert' : undefined}
      title={danger ? <span className="amk-rosso">{label}</span> : label}
      sub={sub}
      subWrap
      onClick={disabled ? undefined : onClick}
      chevron={false}
    />
  );
}

/** Due o tre righe di testo dentro una lista: il motivo di un dubbio, cosa dice la banca. */
export function Testo({ children }: { children: ReactNode }) {
  return <div className="mk-row amk-testo">{children}</div>;
}

/**
 * Le viste (MSeg) con la pillola accesa sempre in vista. Con sei viste (Fisco)
 * "Debiti a rate" stava fuori schermo a destra: accesa ma invisibile. Scorre
 * solo la fila, mai la pagina (niente scrollIntoView), come ViewTabs.
 */
export function VisteInVista({ chiave, children }: { chiave: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const prima = useRef(true);
  useEffect(() => {
    const fila = ref.current?.querySelector<HTMLElement>('.mk-seg');
    const accesa = fila?.querySelector<HTMLElement>('button[aria-pressed="true"]');
    if (!fila || !accesa || fila.scrollWidth <= fila.clientWidth) { prima.current = false; return; }
    const f = fila.getBoundingClientRect();
    const a = accesa.getBoundingClientRect();
    if (a.left >= f.left + 16 && a.right <= f.right - 16) { prima.current = false; return; }
    fila.scrollTo({ left: fila.scrollLeft + (a.left + a.width / 2 - (f.left + f.width / 2)), behavior: prima.current ? 'auto' : 'smooth' });
    prima.current = false;
  }, [chiave]);
  return <div ref={ref}>{children}</div>;
}

/** Un titolo di riga che può andare su due righe (il kit lo taglia a una): per i nomi lunghi che non si accorciano. */
export function DueRighe({ children }: { children: ReactNode }) {
  return <span className="amk-titolo2">{children}</span>;
}

/**
 * Il filtro "Anche gli anni prima del 2026" acceso (si accende dal menu ⋯):
 * una riga sola, che si tocca per spegnerlo. Senza, chi guarda non saprebbe
 * perché i numeri sono diversi dal solito.
 */
export function StoricoAcceso({ year, onOff }: { year: string; onOff: () => void }) {
  return (
    <div className="mk-list">
      <MRow icon="clock" title={`Anche gli anni prima del ${year}`} sub={`Tocca per guardare solo dal ${year} in poi`} onClick={onOff} chevron={false} />
    </div>
  );
}
